import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import apiClient from '../api/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

// ---------------------------------------------------------------------------
// OAuthAuthorizePage — the consent screen MCP clients (Claude Desktop, etc.)
// land on partway through the auth code flow. Reads every OAuth query
// parameter, calls /oauth/authorize/initiate, then either auto-redirects or
// shows the consent UI for the user to approve / deny.
// ---------------------------------------------------------------------------

interface InitiateResponse {
  autoApproved?: boolean;
  needsConsent?: boolean;
  approveRedirect?: string;
  errorRedirect?: string;
  clientId?: string;
  clientName?: string;
  scopes?: string[];
}

const SCOPE_LABELS: Record<string, string> = {
  'mcp:access': 'Read and update your Cadence data via MCP (work items, plans, calendar, drivers, decisions)',
  'mcp:read': 'Read your Cadence data via MCP (work items, plans, calendar, drivers, decisions)',
  'mcp:write': 'Update and create Cadence items via MCP (mutations on the categories above)',
  'offline_access': "Stay connected even when you're offline (refresh access)",
};

export default function OAuthAuthorizePage() {
  const [phase, setPhase] = useState<'loading' | 'consent' | 'error' | 'submitting'>('loading');
  const [data, setData] = useState<InitiateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Pull every OAuth query parameter — these came from the connector and must
  // be passed through to the backend untouched.
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get('client_id') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const responseType = params.get('response_type') || '';
  const scope = params.get('scope') || '';
  const state = params.get('state') || '';
  const codeChallenge = params.get('code_challenge') || '';
  const codeChallengeMethod = params.get('code_challenge_method') || '';
  // RFC 8707 resource indicator. MCP 2025-06-18 mandates clients send it; the
  // backend persists it on the auth code and binds it onto the access-token
  // audience.
  const resource = params.get('resource') || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Defence against the live production case where this component is
      // somehow being mounted with no OAuth params. Without this, the page
      // calls initiate with empty fields, gets "Unknown client_id", and
      // the user sees a confusing error. Whatever upstream bug is causing
      // the empty mount, we don't want to make it look like the OAuth flow
      // failed when in fact it never started.
      if (!clientId && !redirectUri && !responseType) {
        if (cancelled) return;
        setError('No OAuth request parameters were supplied. This page expects to be opened by an MCP client (e.g. Claude Desktop) as part of an authorisation flow. If you arrived here directly, return to your MCP client and start the connection again.');
        setPhase('error');
        try {
          await apiClient.post('/oauth/authorize/initiate', {
            clientId, redirectUri, responseType, scope, state, codeChallenge, codeChallengeMethod, resource,
            _diagnosticHref: window.location.href,
            _diagnosticSearch: window.location.search,
          });
        } catch { /* ignore — diagnostic only */ }
        return;
      }

      try {
        const { data } = await apiClient.post('/oauth/authorize/initiate', {
          clientId, redirectUri, responseType, scope, state, codeChallenge, codeChallengeMethod, resource,
          // Cross-origin Referrer-Policy strips path + query from the referer
          // header, so we echo the full SPA URL + search string back so the
          // missing-fields entry actually tells us what the browser landed on.
          _diagnosticHref: window.location.href,
          _diagnosticSearch: window.location.search,
        });
        if (cancelled) return;
        setData(data);
        if (data.autoApproved && data.approveRedirect) {
          window.location.assign(data.approveRedirect);
          return;
        }
        if (data.errorRedirect) {
          window.location.assign(data.errorRedirect);
          return;
        }
        if (data.needsConsent) {
          setPhase('consent');
          return;
        }
        setError('Unexpected response from authorisation server.');
        setPhase('error');
      } catch (e: any) {
        if (cancelled) return;
        const msg = e?.response?.data?.error_description || e?.response?.data?.error || 'Could not start authorisation.';
        setError(typeof msg === 'string' ? msg : 'Could not start authorisation.');
        setPhase('error');
      }
    })();
    return () => { cancelled = true; };
    // Run once on mount — the OAuth params come from the URL and don't change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decide = async (approve: boolean) => {
    setPhase('submitting');
    const scopes = (data?.scopes ?? []).join(' ');
    try {
      const path = approve ? '/oauth/authorize/approve' : '/oauth/authorize/deny';
      const { data: result } = await apiClient.post(path, {
        clientId, redirectUri, scope: scopes, state, codeChallenge, codeChallengeMethod, resource,
      });
      if (result?.redirect) {
        window.location.assign(result.redirect);
        return;
      }
      setError('No redirect URL returned.');
      setPhase('error');
    } catch (e: any) {
      const msg = e?.response?.data?.error_description || e?.response?.data?.error || 'Could not record decision.';
      setError(typeof msg === 'string' ? msg : 'Could not record decision.');
      setPhase('error');
    }
  };

  if (phase === 'loading' || phase === 'submitting') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {phase === 'submitting' ? 'Recording decision…' : 'Checking authorisation…'}
          </p>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    const isUnknownClient = (error || '').toLowerCase().includes('unknown client');
    return (
      <div className="mx-auto mt-16 max-w-[560px] p-4">
        <Alert variant="error" className="mb-3">
          <AlertDescription>{error || 'Authorisation failed.'}</AlertDescription>
        </Alert>
        {isUnknownClient && (
          <Alert variant="info" className="mb-3">
            <AlertDescription>
              <strong>Diagnostic:</strong> the client the MCP app is using is{' '}
              <code className="break-all font-mono text-xs">{clientId || '(none)'}</code>.
              Cadence has no record of it.
              <br /><br />
              <strong>To fix:</strong> fully quit Claude Desktop (⌘Q on macOS), reopen it,
              then in Settings → Integrations remove the Cadence connector and add it again
              with <code className="font-mono text-xs">https://api.stratiumhq.com/mcp</code>. That triggers a fresh
              registration and the next attempt will succeed.
            </AlertDescription>
          </Alert>
        )}
        <Button variant="ghost" onClick={() => { window.location.href = '/'; }}>Back to Cadence</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-12 max-w-[560px] p-4">
      <Card>
        <CardContent>
          <h1 className="mb-1 text-xl font-semibold tracking-tight">
            Connect {data?.clientName || 'this app'} to Cadence?
          </h1>
          <p className="mb-6 text-sm text-muted-foreground">
            {data?.clientName || 'A connected app'} is requesting access to your Cadence account.
            Review what it will be able to do before approving.
          </p>

          <div className="mb-6 flex flex-col gap-2.5">
            {(data?.scopes ?? []).map((s) => (
              <div key={s} className="flex items-start gap-2">
                <span className="mt-0.5 inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] font-mono">
                  {s}
                </span>
                <p className="text-sm">{SCOPE_LABELS[s] || s}</p>
              </div>
            ))}
          </div>

          <p className="mb-4 block text-xs text-muted-foreground">
            You can revoke access at any time from Connections → Connected apps.
          </p>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => decide(false)}>Deny</Button>
            <Button onClick={() => decide(true)}>Allow</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
